import {
  ApppointmentTabTypes,
  ApppointmentTabTypesEnum,
  Auth,
  DashboardAppointment,
} from '@/@types';
import { withPermission } from '@/components/Permissions';
import { TRACK_EVENTS } from '@/providers/eventAnalytics/trackEvents';
import { env } from '@/constants';
import { locations } from '@/config/locationsData';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HomeCard, HomeCardProps } from './homeCard';
import { eventAnalytics } from '@/providers';

export function AppointmentCard({
  type,
  appointment,
}: {
  type: ApppointmentTabTypes;
  appointment: DashboardAppointment;
}) {
  const navigate = useNavigate();
  const category = useMemo(() => {
    if (type === ApppointmentTabTypesEnum.PAST) {
      return 'Previous appointment';
    }
    if (type === ApppointmentTabTypesEnum.UPCOMING) {
      return 'Upcoming appointment';
    }
    return '';
  }, [type]);
  const buttons = useMemo((): HomeCardProps['buttons'] => {
    if (type === ApppointmentTabTypesEnum.PAST) {
      return [
        {
          variant: 'tertiary',
          label: 'View all',
          onClick: () => {
            eventAnalytics.track(TRACK_EVENTS.HOME_PAGE_CARD_CLICKED, {
              cardName: 'Previous appointments',
              buttonClicked: 'View all',
            });
            if (env.APP_FEATURE_FLAGS.IS_TO_SHOW_NEW_APPOINTMENTS_PAGE) {
              return navigate('/appointments?tab=past');
            }
            return navigate('/appointments/previous');
          },
        },
      ];
    }
    if (type === ApppointmentTabTypesEnum.UPCOMING) {
      return [
        {
          variant: 'tertiary',
          label: 'View all',
          onClick: () => {
            eventAnalytics.track(TRACK_EVENTS.HOME_PAGE_CARD_CLICKED, {
              cardName: 'Upcoming appointments',
              buttonClicked: 'View all',
            });
            if (env.APP_FEATURE_FLAGS.IS_TO_SHOW_NEW_APPOINTMENTS_PAGE) {
              return navigate('/appointments?tab=upcoming');
            }
            return navigate('/appointments/upcoming');
          },
        },
      ];
    }
    return [];
  }, [navigate, type]);

  const Card = withPermission(HomeCard, Auth.Permissions.UserPermissions.VIEW_APPOINTMENTS);

  function getHtmlDescription() {
    const appointmentType = appointment?.type?.toLowerCase() || '';

    if (appointmentType.includes('tele')) {
      return getHtmlVideoVisit();
    } else if (appointmentType.includes('home')) {
      return getHtmlHomeVisit();
    } else if (appointmentType.includes('phone')) {
      return getHtmlPhoneCall();
    } else {
      return getHtmlInPersonVisit();
    }
  }

  function getHtmlInPersonVisit() {
    if (type === ApppointmentTabTypesEnum.PAST) {
      return 'Atria Institute';
    }

    const location =
      appointment?.location?.toLowerCase() !== 'new york' ? locations[1] : locations[0];

    return (
      <>
        Atria Institute .{' '}
        <a
          href={location.directionMaps}
          className='underline'
          target='_blank'
          rel='noreferrer'
          style={{
            textUnderlinePosition: 'under',
            textDecorationSkipInk: 'none',
          }}
        >
          Directions
        </a>
      </>
    );
  }

  const cartTitle = useMemo(() => {
    const startDate = DateTime.fromISO(appointment.start, { zone: 'America/New_York' });
    const endDate = DateTime.fromISO(appointment.end, { zone: 'America/New_York' });

    const formattedDate = startDate.toFormat('MMMM d');

    const startTimeFormat = startDate.minute === 0 ? 'h' : 'h:mm';
    const endTimeFormat = endDate.minute === 0 ? 'h' : 'h:mm';

    const startTime =
      startDate.toFormat(startTimeFormat).toLowerCase() +
      ' ' +
      (startDate.hour < 12 ? 'a.m.' : 'p.m.');
    const endTime =
      endDate.toFormat(endTimeFormat).toLowerCase() + ' ' + (endDate.hour < 12 ? 'a.m.' : 'p.m.');

    return (
      <>
        <p>{formattedDate}</p>
        <p>
          {startTime} - {endTime}
        </p>
      </>
    );
  }, [appointment.start, appointment.end]);

  function getHtmlVideoVisit() {
    if (type === ApppointmentTabTypesEnum.PAST || !appointment?.conferenceLink) {
      return <p>Video visit</p>;
    }
    return (
      <span>
        Video visit .{' '}
        <a
          onClick={() => {
            appointment?.conferenceLink &&
              window.open(appointment?.conferenceLink, '_blank', 'noopener,noreferrer');
          }}
          className='underline cursor-pointer'
          target='_blank'
          rel='noreferrer'
          style={{
            textUnderlinePosition: 'under',
            textDecorationSkipInk: 'none',
          }}
        >
          Zoom Link
        </a>
      </span>
    );
  }

  function getHtmlPhoneCall() {
    if (!appointment?.podPhone) {
      return <span>Phone Call</span>;
    }
    return <span>Phone Call .{appointment?.podPhone ? ` ${appointment?.podPhone}` : ''}</span>;
  }

  function getHtmlHomeVisit() {
    if (!appointment?.address) {
      return <p>Home visit</p>;
    }
    return (
      <p>
        Home visit . {appointment?.addressNumber} {appointment?.address}
      </p>
    );
  }

  return (
    <Card
      category={category}
      htmlTitle={cartTitle}
      htmlDescription={getHtmlDescription()}
      buttons={buttons}
    />
  );
}
